import { flexRender, getCoreRowModel, getPaginationRowModel, useReactTable } from '@tanstack/react-table';
import { useEffect, useId, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import ProjectTableFooter from '@/components/Sherpa/Projects/Project/Table/ProjectTableFooter';
import Checkbox from '@/components/UI/Form/Checkbox';
import LoadingTable from '@/components/UI/loading/LoadingTable';
import styles from '@/components/UI/ReactTable/react-table.module.css';
import { TableRow } from '@/features/ChangeOrder/Summary/Table/TableRow';

interface IChangeOrderTableProps {
  cols: any;
  data: any;
  onRowClick?: (row: any) => void;
  fetching?: boolean;
  pagination?: {
    pageSize: number;
    page: number;
    perPage: number;
    perPageHandler: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    pageChangeHandler: (action: string) => void;
  };
  enableColumnResizing?: boolean;
  headerBorderAccent?: boolean;
  height?: string;
  onRowSelect?: (val: any[]) => void;
  wrapperClassName?: string;
  tableClassName?: string;
}

const getRowSelectionColumn = (baseId: string) => ({
  id: 'rowSelect',
  size: 72,
  minSize: 72,
  maxSize: 72,
  header: (d: any) => (
    <Checkbox
      className='pl-2'
      id={`${baseId}-header`}
      name='ReactTableColumnCheckbox'
      checked={d.table.getIsAllRowsSelected()}
      onChange={d.table.getToggleAllRowsSelectedHandler()}
    />
  ),
  cell: (c: any) => (
    <Checkbox
      className='pl-2'
      id={`${baseId}-row-${c.row.id}`}
      name='ReactTableColumnCheckbox'
      checked={c.row.getIsSelected()}
      onChange={c.row.getToggleSelectedHandler()}
    />
  ),
});

const ChangeOrderTable = ({
  cols,
  data,
  pagination,
  onRowClick,
  onRowSelect,
  fetching,
  headerBorderAccent,
  enableColumnResizing = true,
  wrapperClassName,
  tableClassName,
}: IChangeOrderTableProps) => {
  const checkboxId = useId();
  const rowSelectionColumn = useMemo(() => getRowSelectionColumn(checkboxId), [checkboxId]);
  const columns = useMemo(() => [rowSelectionColumn, ...cols], [cols, rowSelectionColumn]);
  const [rowSelection, setRowSelection] = useState({});
  const [columnVisibility, setColumnVisibility] = useState({});

  const table = useReactTable({
    data,
    columns,
    pageCount: pagination?.pageSize,
    state: {
      columnVisibility,
      rowSelection,
    },
    manualPagination: true,
    onColumnVisibilityChange: setColumnVisibility,
    enableColumnResizing,
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    defaultColumn: {
      minSize: 100,
    },
  });

  useEffect(() => {
    if (onRowSelect && table.getSelectedRowModel().flatRows) {
      onRowSelect(table.getSelectedRowModel().flatRows);
    }
  }, [onRowSelect, table.getSelectedRowModel]);

  if (fetching) {
    return <LoadingTable />;
  }

  return (
    <div className={twMerge(['relative divide-y divide-gray-200 overflow-auto', wrapperClassName])}>
      <table
        className={tableClassName}
        style={{
          width: table.getCenterTotalSize(),
        }}
      >
        <thead className={`bg-gray-100 ${headerBorderAccent && 'border-l-4'}`}>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className='w-full'>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  {...{
                    className: 'relative p-4 text-left text-xs font-bold text-gray-500 capitalize tracking-wider',
                    colSpan: header.colSpan,
                    style: {
                      width: header.column.columnDef.size,
                      maxWidth: header.column.columnDef.maxSize || 'auto',
                      minWidth: header.column.columnDef.minSize,
                    },
                  }}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}

                  {enableColumnResizing && (
                    <div
                      {...{
                        onMouseDown: header.getResizeHandler(),
                        onTouchStart: header.getResizeHandler(),
                        className: `${styles.resizer} ${header.column.getIsResizing() ? styles.isResizing : ''}`,
                      }}
                    />
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        {fetching && (
          <tbody className='bg-white divide-y divide-gray-200 animate-pulse'>
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
              <tr key={num}>
                {Array.from(Array(columns.length).keys()).map((_num, index) => (
                  <td key={index} className='p-6'>
                    <span className='h-2 rounded-md bg-neutralColor-light w-32 block'></span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        )}
        {!fetching && (
          <tbody className='bg-white divide-y divide-gray-200'>
            {table.getRowModel().rows.map((row) => {
              return <TableRow key={row.id} row={row} onRowClick={onRowClick} />;
            })}
          </tbody>
        )}
      </table>
      <div>
        {pagination && (
          <ProjectTableFooter
            pageSize={pagination.pageSize}
            page={pagination.page}
            perPage={pagination.perPage}
            perPageHandler={pagination.perPageHandler}
            pageChangeHandler={pagination.pageChangeHandler}
          />
        )}
      </div>
    </div>
  );
};

export default ChangeOrderTable;
